import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/privacy.scss";

const privacySections = [
  ["information", "Information we collect"],
  ["use", "How we use information"],
  ["cookies", "Cookies & measurement"],
  ["sharing", "How information is shared"],
  ["retention", "Retention & security"],
  ["choices", "Your choices & rights"],
  ["international", "International & children"],
  ["updates", "Updates & contact"],
];

function Privacy() {
  return (
    <main className="policy-page privacy">
      <header className="policy-page__header">
        <p className="policy-page__eyebrow">LEGAL / {siteConfig.wordmark}</p>
        <h1>PRIVACY<br />POLICY</h1>
        <p className="policy-page__lede">
          This policy explains what information we collect, why we use it, and
          the choices available when you browse, shop, or contact us.
        </p>
        <div className="policy-page__meta">
          <span>EFFECTIVE AUGUST 13, 2026</span>
          <span>PRIVACY QUESTIONS: <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a></span>
        </div>
      </header>

      <div className="policy-page__layout">
        <aside className="policy-page__toc">
          <p>ON THIS PAGE</p>
          <nav aria-label="Privacy policy sections">
            {privacySections.map(([id, label], index) => (
              <a key={id} href={`#privacy-${id}`}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                {label}
              </a>
            ))}
          </nav>
        </aside>

        <article className="policy-page__content">
          <div className="policy-page__intro">
            <p>
              This Privacy Policy applies to the {siteConfig.wordmark} online
              store, customer support interactions, and related digital services.
              It does not apply to third-party websites that have their own
              privacy notices.
            </p>
          </div>

          <section id="privacy-information" className="policy-page__section">
            <span className="policy-page__section-number">01</span>
            <h2>INFORMATION WE COLLECT</h2>
            <p>We collect information in three main ways:</p>
            <ul>
              <li><strong>Information you provide.</strong> Contact details, delivery and billing addresses, order details, account preferences, support messages, and marketing choices.</li>
              <li><strong>Transaction information.</strong> Products purchased, discounts, refunds, payment status, and limited payment details supplied by our payment processor. We do not store complete card numbers.</li>
              <li><strong>Device and usage information.</strong> IP address, browser and device type, pages viewed, referring pages, approximate location, and interactions with store features.</li>
            </ul>
            <p>
              If you contact support, we may also receive photographs, product
              serial details, or other information you choose to include so we
              can investigate your request.
            </p>
          </section>

          <section id="privacy-use" className="policy-page__section">
            <span className="policy-page__section-number">02</span>
            <h2>HOW WE USE INFORMATION</h2>
            <p>We use personal information to:</p>
            <ul>
              <li>process payments, fulfill orders, arrange delivery, and manage returns;</li>
              <li>provide customer service, product support, and warranty assistance;</li>
              <li>protect the store, customers, and transactions from fraud or misuse;</li>
              <li>remember preferences and improve site performance, products, and services;</li>
              <li>send essential service messages about orders, security, and support; and</li>
              <li>meet accounting, tax, legal, and regulatory obligations.</li>
            </ul>
            <p>
              We rely on the information to perform our contract with you, pursue
              legitimate business interests, comply with law, and act on your
              consent where consent is required.
            </p>
          </section>

          <section id="privacy-cookies" className="policy-page__section">
            <span className="policy-page__section-number">03</span>
            <h2>COOKIES &amp; MEASUREMENT</h2>
            <p>
              Cookies and similar technologies keep your bag working, remember
              settings, measure site reliability, and help us understand which
              products and pages are useful. Some technologies are necessary for
              checkout and security; others may be optional depending on your
              location.
            </p>
            <p>
              You can limit cookies through your browser controls. Blocking
              essential cookies may prevent sign-in, saved bags, or checkout from
              working as intended. Where a consent control is presented, your
              selection can be changed through that control.
            </p>
          </section>

          <section id="privacy-sharing" className="policy-page__section">
            <span className="policy-page__section-number">04</span>
            <h2>HOW INFORMATION IS SHARED</h2>
            <p>
              We share only the information needed for service providers to carry
              out work on our behalf, including payment processing, fraud
              prevention, fulfillment, delivery, customer support, hosting,
              analytics, and communications. These providers are required to
              protect information and use it for the contracted service.
            </p>
            <p>
              We may also disclose information to comply with law, protect legal
              rights and safety, investigate misuse, or complete a business
              reorganization. We do not sell personal information for money.
            </p>
          </section>

          <section id="privacy-retention" className="policy-page__section">
            <span className="policy-page__section-number">05</span>
            <h2>RETENTION &amp; SECURITY</h2>
            <p>
              We retain information only as long as reasonably necessary for the
              purposes described here, including order support, warranty records,
              fraud prevention, accounting, and legal requirements. Retention
              periods vary by record type and location.
            </p>
            <p>
              We use administrative, technical, and physical safeguards designed
              to protect personal information. No transmission or storage method
              is completely secure, so we cannot guarantee absolute security.
            </p>
          </section>

          <section id="privacy-choices" className="policy-page__section">
            <span className="policy-page__section-number">06</span>
            <h2>YOUR CHOICES &amp; RIGHTS</h2>
            <p>
              Depending on where you live, you may have the right to request
              access, correction, deletion, restriction, portability, or an
              explanation of certain processing. You may also object to some uses
              or withdraw consent without affecting earlier processing.
            </p>
            <div className="policy-page__note">
              <strong>MAKE A PRIVACY REQUEST</strong>
              <p>
                Email <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a> with
                “Privacy Request” in the subject line. We may verify your identity
                before completing a request and will respond within the period
                required by applicable law.
              </p>
            </div>
          </section>

          <section id="privacy-international" className="policy-page__section">
            <span className="policy-page__section-number">07</span>
            <h2>INTERNATIONAL &amp; CHILDREN</h2>
            <p>
              Store providers may process information in countries other than the
              one where you live. Where required, we use recognized safeguards for
              cross-border transfers.
            </p>
            <p>
              Our store is not directed to children under 13, and we do not
              knowingly collect their personal information. A parent or guardian
              who believes a child submitted information should contact us so we
              can review and remove it where appropriate.
            </p>
          </section>

          <section id="privacy-updates" className="policy-page__section">
            <span className="policy-page__section-number">08</span>
            <h2>UPDATES &amp; CONTACT</h2>
            <p>
              We may update this policy when our services or legal obligations
              change. The effective date above shows when the current version
              began. Material changes will be highlighted where required.
            </p>
            <p>
              Questions or complaints can be sent to <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a> or
              to {siteConfig.contact.address}. You can also use our <Link to="/contact">contact form</Link>.
            </p>
          </section>

          <nav className="policy-page__related" aria-label="Related policies">
            <span>RELATED</span>
            <Link to="/terms">TERMS OF SERVICE</Link>
            <Link to="/returns">RETURNS</Link>
            <Link to="/shipping">SHIPPING</Link>
          </nav>
        </article>
      </div>
    </main>
  );
}

export default Privacy;
